import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZVendorApplication, VendorApplication } from '@/contracts/vendor-application';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { submitVendorApplication, uploadVendorAsset } from '@/data/vendorApplications';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle } from 'lucide-react';

const STEPS = [
  { title: 'Company Info', fields: ['company_name', 'company_website'] },
  { title: 'Contact & Product', fields: ['contact_name', 'contact_email', 'product_name'] },
  { title: 'Pricing & Deal', fields: ['current_price', 'proposed_deal_price'] },
  { title: 'Features & Assets', fields: ['key_features'] },
  { title: 'Business Details', fields: ['primary_goal'] },
  { title: 'Review & Submit', fields: ['agreed_to_terms', 'consent_to_processing'] },
];

interface PartnerApplicationFormProps {
  onSuccess: () => void;
}

export default function PartnerApplicationForm({ onSuccess }: PartnerApplicationFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const { toast } = useToast();

  const form = useForm<VendorApplication>({
    resolver: zodResolver(ZVendorApplication),
    defaultValues: {
      service_type: 'product',
      agreed_to_terms: false,
      consent_to_processing: false,
      prior_ltd_experience: false,
      key_features: [],
      integrations: [],
      screenshots: [],
    },
  });

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const onSubmit = async (data: VendorApplication) => {
    setIsSubmitting(true);
    try {
      // Create a temporary ID for file uploads
      const tempId = crypto.randomUUID();
      
      // Upload logo if present
      if (logoFile) {
        const logoUrl = await uploadVendorAsset(logoFile, tempId, 'logo');
        data.logo_url = logoUrl;
      }
      
      // Upload screenshots if present
      if (screenshotFiles.length > 0) {
        const screenshotUrls = await Promise.all(
          screenshotFiles.map(file => uploadVendorAsset(file, tempId, 'screenshot'))
        );
        data.screenshots = screenshotUrls;
      }
      
      await submitVendorApplication(data);
      
      toast({
        title: 'Application Submitted!',
        description: 'We\'ll review your application and get back to you within 5 business days.',
      });
      
      onSuccess();
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    const fields = STEPS[currentStep].fields;
    const isValid = await form.trigger(fields as any);
    if (isValid) setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium">{STEPS[currentStep].title}</span>
          <span className="text-sm text-muted-foreground">Step {currentStep + 1} of {STEPS.length}</span>
        </div>
        <Progress value={progress} />
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Step 1: Company Info */}
        {currentStep === 0 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input id="company_name" {...form.register('company_name')} />
              {form.formState.errors.company_name && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.company_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="company_website">Company Website *</Label>
              <Input id="company_website" type="url" {...form.register('company_website')} />
              {form.formState.errors.company_website && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.company_website.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="company_location">Location</Label>
              <Input id="company_location" {...form.register('company_location')} />
            </div>
            <div>
              <Label htmlFor="company_year_founded">Year Founded</Label>
              <Input id="company_year_founded" type="number" {...form.register('company_year_founded', { valueAsNumber: true })} />
            </div>
          </div>
        )}

        {/* Step 2: Contact & Product */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact_name">Your Full Name *</Label>
              <Input id="contact_name" {...form.register('contact_name')} />
              {form.formState.errors.contact_name && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.contact_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="contact_email">Your Email *</Label>
              <Input id="contact_email" type="email" {...form.register('contact_email')} />
              {form.formState.errors.contact_email && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.contact_email.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="contact_title">Your Title *</Label>
              <Input id="contact_title" {...form.register('contact_title')} />
              {form.formState.errors.contact_title && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.contact_title.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="product_name">Product/Service Name *</Label>
              <Input id="product_name" {...form.register('product_name')} />
              {form.formState.errors.product_name && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.product_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="service_type">Type *</Label>
              <Select onValueChange={(value) => form.setValue('service_type', value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Software/Product</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="education">Education/Course</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="product_category">Category *</Label>
              <Input id="product_category" {...form.register('product_category')} placeholder="e.g., CRM, Marketing, Design" />
              {form.formState.errors.product_category && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.product_category.message}</p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Pricing & Deal */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="product_brief_description">Brief Description (20+ chars) *</Label>
              <Textarea id="product_brief_description" {...form.register('product_brief_description')} rows={3} />
              {form.formState.errors.product_brief_description && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.product_brief_description.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="current_price">Current Price ($) *</Label>
              <Input id="current_price" type="number" step="0.01" {...form.register('current_price', { valueAsNumber: true })} />
              {form.formState.errors.current_price && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.current_price.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="proposed_deal_price">Proposed Deal Price ($) *</Label>
              <Input id="proposed_deal_price" type="number" step="0.01" {...form.register('proposed_deal_price', { valueAsNumber: true })} />
              {form.formState.errors.proposed_deal_price && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.proposed_deal_price.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="pricing_model">Pricing Model *</Label>
              <Input id="pricing_model" {...form.register('pricing_model')} placeholder="e.g., One-time, Monthly, Yearly" />
              {form.formState.errors.pricing_model && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.pricing_model.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="deal_includes">What's Included in Deal? *</Label>
              <Textarea id="deal_includes" {...form.register('deal_includes')} rows={3} />
              {form.formState.errors.deal_includes && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.deal_includes.message}</p>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Features & Assets */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="target_audience">Target Audience *</Label>
              <Input id="target_audience" {...form.register('target_audience')} placeholder="e.g., Real estate agents, Teams of 10+" />
              {form.formState.errors.target_audience && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.target_audience.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="problem_solved">What Problem Does It Solve? *</Label>
              <Textarea id="problem_solved" {...form.register('problem_solved')} rows={3} />
              {form.formState.errors.problem_solved && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.problem_solved.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="key_features">Key Features (comma-separated, min 3) *</Label>
              <Textarea 
                id="key_features" 
                placeholder="Feature 1, Feature 2, Feature 3..."
                onChange={(e) => {
                  const features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                  form.setValue('key_features', features);
                }}
                rows={3}
              />
              {form.formState.errors.key_features && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.key_features.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="logo">Logo (optional)</Label>
              <Input 
                id="logo" 
                type="file" 
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />
            </div>
            <div>
              <Label htmlFor="screenshots">Screenshots (optional, max 5)</Label>
              <Input 
                id="screenshots" 
                type="file" 
                accept="image/*"
                multiple
                onChange={(e) => setScreenshotFiles(Array.from(e.target.files || []).slice(0, 5))}
              />
            </div>
          </div>
        )}

        {/* Step 5: Business Details */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="product_detailed_description">Detailed Description (100+ chars) *</Label>
              <Textarea id="product_detailed_description" {...form.register('product_detailed_description')} rows={5} />
              {form.formState.errors.product_detailed_description && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.product_detailed_description.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="primary_goal">Primary Goal for Partnership *</Label>
              <Textarea id="primary_goal" {...form.register('primary_goal')} rows={3} />
              {form.formState.errors.primary_goal && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.primary_goal.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="customer_count">Number of Customers (optional)</Label>
              <Input id="customer_count" type="number" {...form.register('customer_count', { valueAsNumber: true })} />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="prior_ltd_experience" 
                checked={form.watch('prior_ltd_experience')}
                onCheckedChange={(checked) => form.setValue('prior_ltd_experience', !!checked)}
              />
              <Label htmlFor="prior_ltd_experience">Have you run lifetime deals before?</Label>
            </div>
          </div>
        )}

        {/* Step 6: Review & Submit */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="product_website">Product Website *</Label>
              <Input id="product_website" type="url" {...form.register('product_website')} />
              {form.formState.errors.product_website && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.product_website.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="terms_url">Terms of Service URL *</Label>
              <Input id="terms_url" type="url" {...form.register('terms_url')} />
              {form.formState.errors.terms_url && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.terms_url.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="privacy_url">Privacy Policy URL *</Label>
              <Input id="privacy_url" type="url" {...form.register('privacy_url')} />
              {form.formState.errors.privacy_url && (
                <p className="text-sm text-destructive mt-1">{form.formState.errors.privacy_url.message}</p>
              )}
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="agreed_to_terms" 
                checked={form.watch('agreed_to_terms')}
                onCheckedChange={(checked) => form.setValue('agreed_to_terms', !!checked)}
              />
              <Label htmlFor="agreed_to_terms" className="font-normal">
                I agree to Circle Pro Hub's partner terms and conditions *
              </Label>
            </div>
            {form.formState.errors.agreed_to_terms && (
              <p className="text-sm text-destructive">{form.formState.errors.agreed_to_terms.message}</p>
            )}
            <div className="flex items-start space-x-2">
              <Checkbox 
                id="consent_to_processing" 
                checked={form.watch('consent_to_processing')}
                onCheckedChange={(checked) => form.setValue('consent_to_processing', !!checked)}
              />
              <Label htmlFor="consent_to_processing" className="font-normal">
                I consent to processing of my data for partnership evaluation *
              </Label>
            </div>
            {form.formState.errors.consent_to_processing && (
              <p className="text-sm text-destructive">{form.formState.errors.consent_to_processing.message}</p>
            )}
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button type="button" variant="outline" onClick={prevStep} disabled={currentStep === 0 || isSubmitting}>
            Previous
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button type="button" onClick={nextStep}>
              Next
            </Button>
          ) : (
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Submit Application
                </>
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
